import React, { useEffect, useState } from 'react';
import {
  BookOpen,
  CalendarDays,
  Download,
  FileText,
  FolderOpen,
  GraduationCap,
  HardDrive,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../../services/api';

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Unknown';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

/**
 * Public list of downloadable graduation booklets.
 * Rendered by src/pages/PublicBooklets.jsx.
 */
const BookletList = () => {
  const [booklets, setBooklets] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBooklets();
  }, []);

  const fetchBooklets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/booklets');
      setBooklets(response.data);
    } catch (error) {
      console.error('Error fetching booklets:', error);
      toast.error('Failed to fetch booklets');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = (booklet) => {
    try {
      window.open(`${api.defaults.baseURL}/booklets/download/${booklet.id}`, '_blank');
      toast.success('Download started');
    } catch (error) {
      console.error('Error downloading booklet:', error);
      toast.error('Failed to download booklet');
    }
  };

  if (loading) {
    return (
      <div className="container-page py-6 sm:py-8">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="card">
              <div className="mb-3 h-9 w-9 animate-pulse rounded bg-ink-200" />
              <div className="mb-2 h-4 w-3/4 animate-pulse rounded bg-ink-200" />
              <div className="h-3 w-1/2 animate-pulse rounded bg-ink-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-6 sm:py-8">
      {booklets.length === 0 ? (
        <div className="card mx-auto max-w-md py-12 text-center">
          <span className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-50 text-brand-700">
            <BookOpen className="h-8 w-8" aria-hidden="true" />
          </span>
          <h3 className="mb-2 text-lg font-semibold text-ink-900">No booklets available</h3>
          <p className="text-ink-500">
            Check back later for graduation programme booklets.
          </p>
        </div>
      ) : (
        <>
          <div className="mb-5 flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-lg font-bold text-ink-900">
              Available booklets
              <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 align-middle text-xs font-semibold text-brand-700">
                {booklets.length}
              </span>
            </h2>
            <p className="text-xs text-ink-500">PDF downloads — no login required</p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3 xl:grid-cols-4">
            {booklets.map((booklet) => (
              <article
                key={booklet.id}
                className="card flex flex-col p-4 transition-shadow duration-300 hover:shadow-card-hover"
              >
                <div className="flex-1">
                  <div className="mb-3 flex items-start justify-between gap-2">
                    <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
                      <FileText className="h-5 w-5" aria-hidden="true" />
                    </span>
                    {booklet.graduation_year && (
                      <span className="badge-navy">
                        <GraduationCap className="h-3 w-3" aria-hidden="true" />
                        {booklet.graduation_year}
                      </span>
                    )}
                  </div>

                  <h3 className="mb-1 line-clamp-2 text-base font-semibold text-ink-900">
                    {booklet.title}
                  </h3>

                  {booklet.description && (
                    <p className="mb-2 line-clamp-2 text-xs text-ink-500">
                      {booklet.description}
                    </p>
                  )}

                  <div className="mb-3 space-y-1 text-xs text-ink-500">
                    <p className="flex items-center gap-1.5">
                      <FolderOpen
                        className="h-3.5 w-3.5 flex-shrink-0 text-ink-400"
                        aria-hidden="true"
                      />
                      <span className="truncate">{booklet.file_name}</span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <HardDrive
                        className="h-3.5 w-3.5 flex-shrink-0 text-ink-400"
                        aria-hidden="true"
                      />
                      <span>{formatFileSize(booklet.file_size)}</span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <CalendarDays
                        className="h-3.5 w-3.5 flex-shrink-0 text-ink-400"
                        aria-hidden="true"
                      />
                      <span>{new Date(booklet.created_at).toLocaleDateString()}</span>
                    </p>
                    <p className="flex items-center gap-1.5">
                      <Download
                        className="h-3.5 w-3.5 flex-shrink-0 text-ink-400"
                        aria-hidden="true"
                      />
                      <span>{booklet.download_count || 0} downloads</span>
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => handleDownload(booklet)}
                  className="btn-primary mt-2 w-full text-sm"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download booklet
                </button>
              </article>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BookletList;
